import { Component, OnInit } from '@angular/core';
import { ApiMeta } from '../../_core/_model/api-response';
import {
  HouseCurrencyInventoryTargetDto,
  HouseInventoryTargetsResponseDto,
  HouseMarketMakerDecisionDetailsDto,
  HouseMarketMakerDecisionListItemDto,
  HouseMarketMakerExecutionDetailsDto,
  HouseMarketMakerExecutionListItemDto,
  HouseMarketMakerPairStrategyDto,
  HouseMarketMakerRunDetailsDto,
  HouseMarketMakerRunSummaryDto,
  HouseMarketMakerSettingsDto,
  HouseMarketMakerStatusDto,
  HouseMarketMakerTab,
  HouseMarketMakerWorkerHealthDto,
  UpdateHouseCurrencyInventoryTargetDto,
  UpdateHouseMarketMakerPairStrategyDto,
  UpdateHouseMarketMakerSettingsDto
} from '../../_core/_model/house-market-maker';
import { HouseMarketMakerService } from '../../_core/_services/house-market-maker.service';
import { NotificationService } from '../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-house-market-maker',
  standalone: false,
  templateUrl: './admin-house-market-maker.html',
  styleUrl: './admin-house-market-maker.css'
})
export class AdminHouseMarketMaker implements OnInit {
  readonly tabs: Array<{ key: HouseMarketMakerTab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'settings', label: 'Global Settings' },
    { key: 'strategies', label: 'Pair Strategies' },
    { key: 'inventory', label: 'Inventory Targets' },
    { key: 'runs', label: 'Runs' },
    { key: 'decisions', label: 'Decisions' },
    { key: 'executions', label: 'Executions' }
  ];

  readonly rowsPerPage = 20;
  readonly liveConfirmationPhrase = 'ENABLE LIVE PENDING ORDER FULFILMENT';

  activeTab: HouseMarketMakerTab = 'overview';

  status: HouseMarketMakerStatusDto | null = null;
  settings: HouseMarketMakerSettingsDto | null = null;
  workerHealth: HouseMarketMakerWorkerHealthDto | null = null;
  pairStrategies: HouseMarketMakerPairStrategyDto[] = [];
  inventory: HouseInventoryTargetsResponseDto | null = null;
  runs: HouseMarketMakerRunSummaryDto[] = [];
  decisions: HouseMarketMakerDecisionListItemDto[] = [];
  executions: HouseMarketMakerExecutionListItemDto[] = [];

  selectedRun: HouseMarketMakerRunDetailsDto | null = null;
  selectedDecision: HouseMarketMakerDecisionDetailsDto | null = null;
  selectedExecution: HouseMarketMakerExecutionDetailsDto | null = null;

  statusLoading = false;
  settingsLoading = false;
  strategiesLoading = false;
  inventoryLoading = false;
  runsLoading = false;
  decisionsLoading = false;
  executionsLoading = false;
  detailsLoading = false;

  savingSettings = false;
  savingStrategy = false;
  savingInventory = false;
  runningShadowScan = false;
  runningLiveScan = false;
  togglingLive = false;
  applyingEmergencyAction = false;
  reconcilingExecutions = false;
  reconcilingExecutionId: string | null = null;

  runsMeta: ApiMeta | null = null;
  decisionsMeta: ApiMeta | null = null;
  executionsMeta: ApiMeta | null = null;
  runsPage = 1;
  decisionsPage = 1;
  executionsPage = 1;

  runFilters = { status: '', trigger: '' };
  decisionFilters = {
    runId: '',
    houseCurrencyPairId: '',
    userOrderId: '',
    status: '',
    strategyType: '',
    currency: ''
  };
  executionFilters = { runId: '', decisionId: '', status: '', reconciliationStatus: '' };

  settingsForm: UpdateHouseMarketMakerSettingsDto = this.emptySettingsForm();

  editingStrategy: HouseMarketMakerPairStrategyDto | null = null;
  strategyForm: UpdateHouseMarketMakerPairStrategyDto = this.emptyStrategyForm();

  editingInventory: HouseCurrencyInventoryTargetDto | null = null;
  inventoryForm: UpdateHouseCurrencyInventoryTargetDto = this.emptyInventoryForm();

  liveControlVisible = false;
  liveEnableConfirmation = '';
  liveEnableReason = '';
  liveDisableReason = '';

  emergencyControlVisible = false;
  emergencyReason = '';
  emergencyClearNote = '';

  constructor(
    private readonly marketMaker: HouseMarketMakerService,
    private readonly notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadStatus();
    this.loadSettings();
    this.loadPairStrategies();
    this.loadInventory();
    this.loadRuns(1);
  }

  setTab(tab: HouseMarketMakerTab): void {
    this.activeTab = tab;

    switch (tab) {
      case 'overview':
        this.loadStatus();
        this.loadRuns(1);
        break;
      case 'settings':
        this.loadSettings();
        break;
      case 'strategies':
        this.loadPairStrategies();
        break;
      case 'inventory':
        this.loadInventory();
        break;
      case 'runs':
        this.loadRuns(this.runsPage);
        break;
      case 'decisions':
        this.loadDecisions(this.decisionsPage);
        break;
      case 'executions':
        this.loadExecutions(this.executionsPage);
        break;
    }
  }

  refreshCurrentTab(): void {
    this.setTab(this.activeTab);
  }

  loadStatus(): void {
    this.statusLoading = true;
    this.marketMaker.getStatus().subscribe({
      next: res => {
        this.status = res.data || null;
        this.workerHealth = res.data?.workerHealth || null;
        this.statusLoading = false;
      },
      error: error => {
        this.statusLoading = false;
        this.showError(error, 'Unable to load market-maker status.');
      }
    });
  }

  loadWorkerHealth(): void {
    this.statusLoading = true;
    this.marketMaker.getWorkerHealth().subscribe({
      next: res => {
        this.workerHealth = res.data || null;
        this.statusLoading = false;
      },
      error: error => {
        this.statusLoading = false;
        this.showError(error, 'Unable to load worker health.');
      }
    });
  }

  loadSettings(): void {
    this.settingsLoading = true;
    this.marketMaker.getSettings().subscribe({
      next: res => {
        this.settings = res.data || null;
        if (res.data) this.settingsForm = this.toSettingsForm(res.data);
        this.settingsLoading = false;
      },
      error: error => {
        this.settingsLoading = false;
        this.showError(error, 'Unable to load market-maker settings.');
      }
    });
  }

  saveSettings(): void {
    const payload = this.normalizeSettingsForm(this.settingsForm);
    const validation = this.validateSettings(payload);
    if (validation) {
      this.notify.showWarning(validation, 'Market Maker');
      return;
    }

    this.savingSettings = true;
    this.marketMaker.updateSettings(payload).subscribe({
      next: res => {
        this.settings = res.data;
        this.settingsForm = this.toSettingsForm(res.data);
        this.savingSettings = false;
        this.notify.showSuccess(res.message || 'Market-maker settings updated.', 'Market Maker');
        this.loadStatus();
      },
      error: error => {
        this.savingSettings = false;
        this.showError(error, 'Unable to update market-maker settings.');
      }
    });
  }

  loadPairStrategies(): void {
    this.strategiesLoading = true;
    this.marketMaker.getPairStrategies().subscribe({
      next: res => {
        this.pairStrategies = res.data || [];
        this.strategiesLoading = false;
      },
      error: error => {
        this.strategiesLoading = false;
        this.showError(error, 'Unable to load pair strategies.');
      }
    });
  }

  editStrategy(strategy: HouseMarketMakerPairStrategyDto): void {
    this.editingStrategy = strategy;
    this.strategyForm = {
      isEnabled: strategy.isEnabled,
      priority: strategy.priority,
      enablePendingOrderFulfilment: strategy.enablePendingOrderFulfilment,
      enableLivePendingOrderFulfilment: strategy.enableLivePendingOrderFulfilment,
      enableInventoryRebalancing: strategy.enableInventoryRebalancing,
      enableMarketSeeding: strategy.enableMarketSeeding,
      enableForecastPositioning: strategy.enableForecastPositioning,
      minimumNetProfitReferenceAmount: strategy.minimumNetProfitReferenceAmount ?? null,
      minimumProfitMarginBasisPoints: strategy.minimumProfitMarginBasisPoints ?? null,
      maximumOrderFromAmount: strategy.maximumOrderFromAmount ?? null,
      maximumOpenExposureReferenceAmount: strategy.maximumOpenExposureReferenceAmount ?? null,
      minimumForecastConfidence: strategy.minimumForecastConfidence ?? null,
      orderLifetimeMinutes: strategy.orderLifetimeMinutes ?? null
    };
  }

  cancelStrategyEdit(): void {
    this.editingStrategy = null;
    this.strategyForm = this.emptyStrategyForm();
  }

  saveStrategy(): void {
    if (!this.editingStrategy) return;

    const payload = this.normalizeStrategyForm(this.strategyForm);
    const validation = this.validateStrategy(payload);
    if (validation) {
      this.notify.showWarning(validation, 'Market Maker');
      return;
    }

    this.savingStrategy = true;
    this.marketMaker.updatePairStrategy(this.editingStrategy.id, payload).subscribe({
      next: res => {
        const updated = res.data;
        this.pairStrategies = this.pairStrategies.map(item => item.id === updated.id ? updated : item);
        this.savingStrategy = false;
        this.cancelStrategyEdit();
        this.notify.showSuccess(res.message || 'Pair strategy updated.', 'Market Maker');
        this.loadStatus();
      },
      error: error => {
        this.savingStrategy = false;
        this.showError(error, 'Unable to update pair strategy.');
      }
    });
  }

  loadInventory(): void {
    this.inventoryLoading = true;
    this.marketMaker.getInventoryTargets().subscribe({
      next: res => {
        this.inventory = res.data || null;
        this.inventoryLoading = false;
      },
      error: error => {
        this.inventoryLoading = false;
        this.showError(error, 'Unable to load inventory targets.');
      }
    });
  }

  editInventoryTarget(target: HouseCurrencyInventoryTargetDto): void {
    this.editingInventory = target;
    this.inventoryForm = {
      isEnabled: target.isEnabled,
      priority: target.priority,
      targetAllocationPercentage: target.targetAllocationPercentage,
      rebalanceTolerancePercentage: target.rebalanceTolerancePercentage,
      minimumReserveAmount: target.minimumReserveAmount,
      maximumInventoryReferenceAmount: target.maximumInventoryReferenceAmount ?? null
    };
  }

  cancelInventoryEdit(): void {
    this.editingInventory = null;
    this.inventoryForm = this.emptyInventoryForm();
  }

  saveInventoryTarget(): void {
    if (!this.editingInventory || !this.inventory) return;

    const payload = this.normalizeInventoryForm(this.inventoryForm);
    const validation = this.validateInventoryTarget(this.editingInventory, payload);
    if (validation) {
      this.notify.showWarning(validation, 'Market Maker');
      return;
    }

    this.savingInventory = true;
    this.marketMaker.updateInventoryTarget(this.editingInventory.currency, payload).subscribe({
      next: res => {
        const updated = res.data;
        if (this.inventory) {
          this.inventory = {
            ...this.inventory,
            items: this.inventory.items.map(item => item.id === updated.id ? updated : item)
          };
        }
        this.savingInventory = false;
        this.cancelInventoryEdit();
        this.notify.showSuccess(res.message || 'Inventory target updated.', 'Market Maker');
        this.loadInventory();
        this.loadStatus();
      },
      error: error => {
        this.savingInventory = false;
        this.showError(error, 'Unable to update inventory target.');
      }
    });
  }

  runShadowScan(): void {
    if (this.runningShadowScan || this.runningLiveScan) return;

    this.runningShadowScan = true;
    this.marketMaker.runShadowScan().subscribe({
      next: res => {
        this.runningShadowScan = false;
        const run = res.data;
        this.notify.showSuccess(
          `${run.runReference}: ${run.wouldExecuteCount} executable, ${run.rejectedCount} rejected.`,
          'Shadow Scan Completed'
        );
        if (run.warnings?.length) {
          this.notify.showWarning(run.warnings.join(' '), 'Shadow Scan Warnings');
        }
        this.loadStatus();
        this.loadRuns(1);
      },
      error: error => {
        this.runningShadowScan = false;
        this.showError(error, 'Unable to run the shadow scan.');
      }
    });
  }

  runLiveScan(): void {
    if (!this.status?.isLiveExecutionEnabled || this.status.isShadowMode) {
      this.notify.showWarning('Live execution must be enabled before a live scan can run.', 'Market Maker');
      return;
    }

    const confirmed = window.confirm(
      'Run a live pending-order fulfilment scan now? Eligible orders may be matched using House liquidity.'
    );
    if (!confirmed) return;

    this.runningLiveScan = true;
    this.marketMaker.runLiveScan().subscribe({
      next: res => {
        this.runningLiveScan = false;
        const run = res.data;
        this.notify.showSuccess(
          `${run.runReference}: ${run.opportunitiesFound} opportunities evaluated.`,
          'Live Scan Completed'
        );
        if (run.warnings?.length) {
          this.notify.showWarning(run.warnings.join(' '), 'Live Scan Warnings');
        }
        this.loadStatus();
        this.loadRuns(1);
        this.loadExecutions(1);
      },
      error: error => {
        this.runningLiveScan = false;
        this.showError(error, 'Unable to run the live scan.');
      }
    });
  }

  openLiveControls(): void {
    this.liveControlVisible = true;
    this.liveEnableConfirmation = '';
    this.liveEnableReason = '';
    this.liveDisableReason = '';
  }

  closeLiveControls(): void {
    if (this.togglingLive) return;
    this.liveControlVisible = false;
  }

  enableLiveExecution(): void {
    if (this.liveEnableConfirmation.trim() !== this.liveConfirmationPhrase) {
      this.notify.showWarning('Enter the exact confirmation phrase before enabling live execution.', 'Market Maker');
      return;
    }
    if (this.liveEnableReason.trim().length < 10) {
      this.notify.showWarning('Provide a clear reason of at least 10 characters.', 'Market Maker');
      return;
    }

    this.togglingLive = true;
    this.marketMaker.enableLiveExecution({
      confirmation: this.liveEnableConfirmation.trim(),
      reason: this.liveEnableReason.trim()
    }).subscribe({
      next: res => {
        this.settings = res.data;
        this.settingsForm = this.toSettingsForm(res.data);
        this.togglingLive = false;
        this.liveControlVisible = false;
        this.notify.showSuccess(res.message || 'Live execution enabled.', 'Market Maker');
        this.loadStatus();
      },
      error: error => {
        this.togglingLive = false;
        this.showError(error, 'Unable to enable live execution.');
      }
    });
  }

  disableLiveExecution(): void {
    this.togglingLive = true;
    this.marketMaker.disableLiveExecution({
      reason: this.liveDisableReason.trim() || null
    }).subscribe({
      next: res => {
        this.settings = res.data;
        this.settingsForm = this.toSettingsForm(res.data);
        this.togglingLive = false;
        this.liveControlVisible = false;
        this.notify.showSuccess(res.message || 'Live execution disabled.', 'Market Maker');
        this.loadStatus();
      },
      error: error => {
        this.togglingLive = false;
        this.showError(error, 'Unable to disable live execution.');
      }
    });
  }

  openEmergencyControls(): void {
    this.emergencyControlVisible = true;
    this.emergencyReason = '';
    this.emergencyClearNote = '';
  }

  closeEmergencyControls(): void {
    if (this.applyingEmergencyAction) return;
    this.emergencyControlVisible = false;
  }

  emergencyStop(): void {
    if (this.emergencyReason.trim().length < 10) {
      this.notify.showWarning('Provide an emergency-stop reason of at least 10 characters.', 'Market Maker');
      return;
    }

    const confirmed = window.confirm(
      'Emergency-stop the House Market Maker? This disables the engine and live execution immediately.'
    );
    if (!confirmed) return;

    this.applyingEmergencyAction = true;
    this.marketMaker.emergencyStop({ reason: this.emergencyReason.trim() }).subscribe({
      next: res => {
        this.settings = res.data;
        this.settingsForm = this.toSettingsForm(res.data);
        this.applyingEmergencyAction = false;
        this.emergencyControlVisible = false;
        this.notify.showWarning(res.message || 'Market Maker emergency-stopped.', 'Emergency Stop');
        this.loadStatus();
      },
      error: error => {
        this.applyingEmergencyAction = false;
        this.showError(error, 'Unable to emergency-stop the Market Maker.');
      }
    });
  }

  clearEmergencyStop(): void {
    const confirmed = window.confirm(
      'Clear the emergency stop? The engine will remain disabled until deliberately enabled in settings.'
    );
    if (!confirmed) return;

    this.applyingEmergencyAction = true;
    this.marketMaker.clearEmergencyStop({ note: this.emergencyClearNote.trim() || null }).subscribe({
      next: res => {
        this.settings = res.data;
        this.settingsForm = this.toSettingsForm(res.data);
        this.applyingEmergencyAction = false;
        this.emergencyControlVisible = false;
        this.notify.showSuccess(res.message || 'Emergency stop cleared.', 'Market Maker');
        this.loadStatus();
      },
      error: error => {
        this.applyingEmergencyAction = false;
        this.showError(error, 'Unable to clear the emergency stop.');
      }
    });
  }

  loadRuns(page = 1): void {
    this.runsPage = page;
    this.runsLoading = true;
    this.marketMaker.getRuns({
      status: this.runFilters.status,
      trigger: this.runFilters.trigger,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.runs = res.data || [];
        this.runsMeta = res.meta || null;
        this.runsLoading = false;
      },
      error: error => {
        this.runsLoading = false;
        this.showError(error, 'Unable to load market-maker runs.');
      }
    });
  }

  viewRun(run: HouseMarketMakerRunSummaryDto): void {
    this.detailsLoading = true;
    this.selectedRun = null;
    this.marketMaker.getRun(run.id).subscribe({
      next: res => {
        this.selectedRun = res.data || null;
        this.detailsLoading = false;
      },
      error: error => {
        this.detailsLoading = false;
        this.showError(error, 'Unable to load run details.');
      }
    });
  }

  closeRunDetails(): void {
    this.selectedRun = null;
  }

  openRunDecisions(run: HouseMarketMakerRunSummaryDto | HouseMarketMakerRunDetailsDto): void {
    this.decisionFilters.runId = run.id;
    this.activeTab = 'decisions';
    this.loadDecisions(1);
  }

  openRunExecutions(run: HouseMarketMakerRunSummaryDto | HouseMarketMakerRunDetailsDto): void {
    this.executionFilters.runId = run.id;
    this.activeTab = 'executions';
    this.loadExecutions(1);
  }

  loadDecisions(page = 1): void {
    this.decisionsPage = page;
    this.decisionsLoading = true;
    this.marketMaker.getDecisions({
      ...this.decisionFilters,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.decisions = res.data || [];
        this.decisionsMeta = res.meta || null;
        this.decisionsLoading = false;
      },
      error: error => {
        this.decisionsLoading = false;
        this.showError(error, 'Unable to load market-maker decisions.');
      }
    });
  }

  resetDecisionFilters(): void {
    this.decisionFilters = {
      runId: '',
      houseCurrencyPairId: '',
      userOrderId: '',
      status: '',
      strategyType: '',
      currency: ''
    };
    this.loadDecisions(1);
  }

  viewDecision(decision: HouseMarketMakerDecisionListItemDto): void {
    this.detailsLoading = true;
    this.selectedDecision = null;
    this.marketMaker.getDecision(decision.id).subscribe({
      next: res => {
        this.selectedDecision = res.data || null;
        this.detailsLoading = false;
      },
      error: error => {
        this.detailsLoading = false;
        this.showError(error, 'Unable to load decision details.');
      }
    });
  }

  closeDecisionDetails(): void {
    this.selectedDecision = null;
  }

  openDecisionExecution(decision: HouseMarketMakerDecisionListItemDto | HouseMarketMakerDecisionDetailsDto): void {
    this.executionFilters.decisionId = decision.id;
    this.activeTab = 'executions';
    this.loadExecutions(1);
  }

  loadExecutions(page = 1): void {
    this.executionsPage = page;
    this.executionsLoading = true;
    this.marketMaker.getExecutions({
      ...this.executionFilters,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.executions = res.data || [];
        this.executionsMeta = res.meta || null;
        this.executionsLoading = false;
      },
      error: error => {
        this.executionsLoading = false;
        this.showError(error, 'Unable to load market-maker executions.');
      }
    });
  }

  resetExecutionFilters(): void {
    this.executionFilters = { runId: '', decisionId: '', status: '', reconciliationStatus: '' };
    this.loadExecutions(1);
  }

  viewExecution(execution: HouseMarketMakerExecutionListItemDto): void {
    this.detailsLoading = true;
    this.selectedExecution = null;
    this.marketMaker.getExecution(execution.id).subscribe({
      next: res => {
        this.selectedExecution = res.data || null;
        this.detailsLoading = false;
      },
      error: error => {
        this.detailsLoading = false;
        this.showError(error, 'Unable to load execution details.');
      }
    });
  }

  closeExecutionDetails(): void {
    this.selectedExecution = null;
  }


  reconcileOutstandingExecutions(): void {
    if (this.reconcilingExecutions) return;

    this.reconcilingExecutions = true;
    this.marketMaker.reconcileExecutions().subscribe({
      next: res => {
        this.reconcilingExecutions = false;
        const result = res.data;
        this.notify.showSuccess(
          `${result.reconciledCount} execution(s) reconciled; ${result.failedCount} failed.`,
          'Settlement Reconciliation'
        );
        if (result.warnings?.length) {
          this.notify.showWarning(result.warnings.join(' '), 'Reconciliation Warnings');
        }
        this.loadExecutions(this.executionsPage);
        this.loadRuns(this.runsPage);
      },
      error: error => {
        this.reconcilingExecutions = false;
        this.showError(error, 'Unable to reconcile market-maker executions.');
      }
    });
  }

  reconcileExecution(execution: HouseMarketMakerExecutionListItemDto | HouseMarketMakerExecutionDetailsDto): void {
    if (this.reconcilingExecutionId) return;

    this.reconcilingExecutionId = execution.id;
    this.marketMaker.reconcileExecution(execution.id).subscribe({
      next: res => {
        this.reconcilingExecutionId = null;
        const result = res.data;
        this.notify.showSuccess(
          `${result.executionReference} is ${result.reconciliationStatus}.`,
          'Execution Reconciled'
        );
        this.loadExecutions(this.executionsPage);
        this.loadRuns(this.runsPage);
        if (this.selectedExecution?.id === execution.id) {
          this.marketMaker.getExecution(execution.id).subscribe({
            next: details => this.selectedExecution = details.data || null,
            error: error => this.showError(error, 'Unable to refresh execution details.')
          });
        }
      },
      error: error => {
        this.reconcilingExecutionId = null;
        this.showError(error, 'Unable to reconcile this execution.');
      }
    });
  }

  get executionModeLabel(): string {
    if (this.status?.isEmergencyStopped) return 'Emergency stopped';
    if (this.status?.isLiveExecutionEnabled && !this.status?.isShadowMode) return 'Live pending fulfilment';
    if (this.status?.isEnabled) return 'Shadow evaluation';
    return 'Disabled';
  }

  get enabledLivePairCount(): number {
    return this.pairStrategies.filter(item => item.isEnabled && item.enableLivePendingOrderFulfilment).length;
  }

  get inventoryAllocationClass(): string {
    const allocation = this.inventory?.enabledTargetAllocationPercentage ?? 0;
    if (allocation > 100) return 'text-bg-danger';
    if (allocation < 99.99) return 'text-bg-warning text-dark';
    return 'text-bg-success';
  }

  get liveEnableReady(): boolean {
    return this.liveEnableConfirmation.trim() === this.liveConfirmationPhrase
      && this.liveEnableReason.trim().length >= 10;
  }

  statusClass(value?: string | null): string {
    const normalized = (value || '').toLowerCase();
    if (['completed', 'healthy', 'active', 'executed', 'wouldexecute', 'idle', 'matched', 'settled'].includes(normalized)) {
      return 'text-bg-success';
    }
    if (['running', 'matching', 'reserving', 'revalidating', 'starting', 'waitingforlock', 'houseordercreated'].includes(normalized)) {
      return 'text-bg-primary';
    }
    if (['failed', 'faulted', 'rejected', 'executionfailed', 'critical', 'emergencystopped', 'settlementfailed', 'reversed', 'cancelled'].includes(normalized)) {
      return 'text-bg-danger';
    }
    if (['completedwithwarnings', 'warning', 'stale', 'disabled', 'skipped', 'pending', 'degraded', 'partiallymatched', 'awaitingsettlement', 'partiallysettled', 'settledwithincompletevaluation', 'notstarted'].includes(normalized)) {
      return 'text-bg-warning text-dark';
    }
    return 'text-bg-secondary';
  }

  profitVarianceClass(value?: number | null, complete = true): string {
    if (!complete) return 'text-warning';
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'text-muted';
    return Number(value) >= 0 ? 'text-success' : 'text-danger';
  }

  checkClass(passed: boolean): string {
    return passed ? 'text-bg-success' : 'text-bg-danger';
  }

  formatDate(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-CA');
  }

  formatMoney(value?: number | null, currency?: string | null): string {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    const code = (currency || this.status?.profitReferenceCurrency || 'CAD').toUpperCase();
    try {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 2
      }).format(Number(value));
    } catch {
      return `${Number(value).toLocaleString('en-CA', { maximumFractionDigits: 2 })} ${code}`;
    }
  }

  formatNumber(value?: number | null, maximumFractionDigits = 4): string {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    return Number(value).toLocaleString('en-CA', { maximumFractionDigits });
  }

  formatPercent(value?: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    return `${Number(value).toLocaleString('en-CA', { maximumFractionDigits: 2 })}%`;
  }

  prettyJson(value?: string | null): string {
    if (!value?.trim()) return 'No diagnostic payload recorded.';
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  private toSettingsForm(settings: HouseMarketMakerSettingsDto): UpdateHouseMarketMakerSettingsDto {
    return {
      isEnabled: settings.isEnabled,
      isShadowMode: settings.isShadowMode,
      profitReferenceCurrency: settings.profitReferenceCurrency,
      scanIntervalSeconds: settings.scanIntervalSeconds,
      inactivityThresholdMinutes: settings.inactivityThresholdMinutes,
      minimumNetProfitReferenceAmount: settings.minimumNetProfitReferenceAmount,
      minimumProfitMarginBasisPoints: settings.minimumProfitMarginBasisPoints,
      maximumSingleOrderReferenceAmount: settings.maximumSingleOrderReferenceAmount,
      maximumDailyVolumeReferenceAmount: settings.maximumDailyVolumeReferenceAmount,
      maximumDailyLossReferenceAmount: settings.maximumDailyLossReferenceAmount,
      maximumOpenHouseOrders: settings.maximumOpenHouseOrders,
      minimumForecastConfidence: settings.minimumForecastConfidence,
      defaultOrderLifetimeMinutes: settings.defaultOrderLifetimeMinutes,
      enablePendingOrderFulfilment: settings.enablePendingOrderFulfilment,
      enableInventoryRebalancing: settings.enableInventoryRebalancing,
      enableMarketSeeding: settings.enableMarketSeeding,
      enableForecastPositioning: settings.enableForecastPositioning
    };
  }

  private normalizeSettingsForm(form: UpdateHouseMarketMakerSettingsDto): UpdateHouseMarketMakerSettingsDto {
    return {
      ...form,
      profitReferenceCurrency: (form.profitReferenceCurrency || '').trim().toUpperCase(),
      scanIntervalSeconds: Number(form.scanIntervalSeconds),
      inactivityThresholdMinutes: Number(form.inactivityThresholdMinutes),
      minimumNetProfitReferenceAmount: Number(form.minimumNetProfitReferenceAmount),
      minimumProfitMarginBasisPoints: Number(form.minimumProfitMarginBasisPoints),
      maximumSingleOrderReferenceAmount: Number(form.maximumSingleOrderReferenceAmount),
      maximumDailyVolumeReferenceAmount: Number(form.maximumDailyVolumeReferenceAmount),
      maximumDailyLossReferenceAmount: Number(form.maximumDailyLossReferenceAmount),
      maximumOpenHouseOrders: Number(form.maximumOpenHouseOrders),
      minimumForecastConfidence: Number(form.minimumForecastConfidence),
      defaultOrderLifetimeMinutes: Number(form.defaultOrderLifetimeMinutes)
    };
  }

  private validateSettings(form: UpdateHouseMarketMakerSettingsDto): string | null {
    if (!/^[A-Z]{3}$/.test(form.profitReferenceCurrency)) return 'Profit reference currency must be a three-letter code.';
    if (!Number.isFinite(form.scanIntervalSeconds) || form.scanIntervalSeconds < 10 || form.scanIntervalSeconds > 86400) return 'Scan interval must be between 10 and 86,400 seconds.';
    if (!Number.isFinite(form.inactivityThresholdMinutes) || form.inactivityThresholdMinutes < 1 || form.inactivityThresholdMinutes > 10080) return 'Inactivity threshold must be between 1 and 10,080 minutes.';
    if (form.minimumNetProfitReferenceAmount < 0) return 'Minimum net profit cannot be negative.';
    if (form.minimumProfitMarginBasisPoints < 0) return 'Minimum profit margin cannot be negative.';
    if (form.maximumSingleOrderReferenceAmount < 0 || form.maximumDailyVolumeReferenceAmount < 0) return 'Execution limits cannot be negative.';
    if (form.maximumDailyLossReferenceAmount < 0) return 'Maximum daily loss cannot be negative.';
    if (form.maximumOpenHouseOrders < 0 || form.maximumOpenHouseOrders > 100000) return 'Maximum open House orders must be between 0 and 100,000.';
    if (form.minimumForecastConfidence < 0 || form.minimumForecastConfidence > 1) return 'Forecast confidence must be between 0 and 1.';
    if (form.defaultOrderLifetimeMinutes < 1 || form.defaultOrderLifetimeMinutes > 1440) return 'Default order lifetime must be between 1 and 1,440 minutes.';
    return null;
  }

  private normalizeStrategyForm(form: UpdateHouseMarketMakerPairStrategyDto): UpdateHouseMarketMakerPairStrategyDto {
    return {
      ...form,
      priority: Number(form.priority),
      minimumNetProfitReferenceAmount: this.nullableNonNegative(form.minimumNetProfitReferenceAmount),
      minimumProfitMarginBasisPoints: this.nullableNonNegative(form.minimumProfitMarginBasisPoints),
      maximumOrderFromAmount: this.nullableNonNegative(form.maximumOrderFromAmount),
      maximumOpenExposureReferenceAmount: this.nullableNonNegative(form.maximumOpenExposureReferenceAmount),
      minimumForecastConfidence: this.nullableNonNegative(form.minimumForecastConfidence),
      orderLifetimeMinutes: this.nullablePositiveInteger(form.orderLifetimeMinutes)
    };
  }

  private validateStrategy(form: UpdateHouseMarketMakerPairStrategyDto): string | null {
    if (!Number.isInteger(form.priority) || form.priority < 0 || form.priority > 100000) return 'Priority must be a whole number between 0 and 100,000.';
    if (form.minimumForecastConfidence !== null && form.minimumForecastConfidence !== undefined
      && (form.minimumForecastConfidence < 0 || form.minimumForecastConfidence > 1)) return 'Forecast confidence must be between 0 and 1.';
    if (form.orderLifetimeMinutes !== null && form.orderLifetimeMinutes !== undefined
      && (form.orderLifetimeMinutes < 1 || form.orderLifetimeMinutes > 1440)) return 'Order lifetime must be between 1 and 1,440 minutes.';
    if (form.enableLivePendingOrderFulfilment && !form.enablePendingOrderFulfilment) {
      return 'Pending-order fulfilment must be enabled before its live switch can be enabled.';
    }
    if (form.enableLivePendingOrderFulfilment && !form.isEnabled) {
      return 'The pair strategy must be enabled before live fulfilment can be enabled.';
    }
    return null;
  }

  private normalizeInventoryForm(form: UpdateHouseCurrencyInventoryTargetDto): UpdateHouseCurrencyInventoryTargetDto {
    return {
      ...form,
      priority: Number(form.priority),
      targetAllocationPercentage: Number(form.targetAllocationPercentage),
      rebalanceTolerancePercentage: Number(form.rebalanceTolerancePercentage),
      minimumReserveAmount: Number(form.minimumReserveAmount),
      maximumInventoryReferenceAmount: this.nullableNonNegative(form.maximumInventoryReferenceAmount)
    };
  }

  private validateInventoryTarget(
    target: HouseCurrencyInventoryTargetDto,
    form: UpdateHouseCurrencyInventoryTargetDto
  ): string | null {
    if (!Number.isInteger(form.priority) || form.priority < 0 || form.priority > 100000) return 'Priority must be a whole number between 0 and 100,000.';
    if (form.targetAllocationPercentage < 0 || form.targetAllocationPercentage > 100) return 'Target allocation must be between 0% and 100%.';
    if (form.rebalanceTolerancePercentage < 0 || form.rebalanceTolerancePercentage > 100) return 'Rebalance tolerance must be between 0% and 100%.';
    if (form.minimumReserveAmount < 0) return 'Minimum reserve cannot be negative.';

    const otherEnabledTotal = (this.inventory?.items || [])
      .filter(item => item.id !== target.id && item.isEnabled)
      .reduce((sum, item) => sum + Number(item.targetAllocationPercentage || 0), 0);
    const projectedTotal = otherEnabledTotal + (form.isEnabled ? form.targetAllocationPercentage : 0);
    if (projectedTotal > 100.0001) {
      return `Enabled allocations would total ${projectedTotal.toFixed(2)}%. They cannot exceed 100%.`;
    }
    return null;
  }

  private emptySettingsForm(): UpdateHouseMarketMakerSettingsDto {
    return {
      isEnabled: false,
      isShadowMode: true,
      profitReferenceCurrency: 'USD',
      scanIntervalSeconds: 300,
      inactivityThresholdMinutes: 30,
      minimumNetProfitReferenceAmount: 0,
      minimumProfitMarginBasisPoints: 0,
      maximumSingleOrderReferenceAmount: 0,
      maximumDailyVolumeReferenceAmount: 0,
      maximumDailyLossReferenceAmount: 0,
      maximumOpenHouseOrders: 0,
      minimumForecastConfidence: 0,
      defaultOrderLifetimeMinutes: 15,
      enablePendingOrderFulfilment: true,
      enableInventoryRebalancing: false,
      enableMarketSeeding: false,
      enableForecastPositioning: false
    };
  }

  private emptyStrategyForm(): UpdateHouseMarketMakerPairStrategyDto {
    return {
      isEnabled: false,
      priority: 0,
      enablePendingOrderFulfilment: true,
      enableLivePendingOrderFulfilment: false,
      enableInventoryRebalancing: false,
      enableMarketSeeding: false,
      enableForecastPositioning: false,
      minimumNetProfitReferenceAmount: null,
      minimumProfitMarginBasisPoints: null,
      maximumOrderFromAmount: null,
      maximumOpenExposureReferenceAmount: null,
      minimumForecastConfidence: null,
      orderLifetimeMinutes: null
    };
  }

  private emptyInventoryForm(): UpdateHouseCurrencyInventoryTargetDto {
    return {
      isEnabled: false,
      priority: 0,
      targetAllocationPercentage: 0,
      rebalanceTolerancePercentage: 0,
      minimumReserveAmount: 0,
      maximumInventoryReferenceAmount: null
    };
  }

  private nullablePositive(value: number | null | undefined): number | null {
    if (value === null || value === undefined || value === ('' as any)) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private nullableNonNegative(value: number | null | undefined): number | null {
    if (value === null || value === undefined || value === ('' as any)) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  private nullablePositiveInteger(value: number | null | undefined): number | null {
    const parsed = this.nullablePositive(value);
    return parsed === null ? null : Math.max(1, Math.round(parsed));
  }

  private showError(error: any, fallback: string): void {
    const message =
      error?.error?.error?.details ||
      error?.error?.error?.message ||
      error?.error?.Error?.Details ||
      error?.error?.details ||
      error?.error?.message ||
      error?.message ||
      fallback;

    this.notify.showError(message, 'House Market Maker');
  }
}
